import { Button } from "@chakra-ui/button";
import { Box } from "@chakra-ui/layout";
import { TreeSelect, TreeSelectProps } from "antd";
import { DataNode } from "antd/lib/tree";
import useGetContasCategorias from "features/contas-categorias/hooks/useGetContasCategorias";
import { CategoriaFinanceiraResponse } from "features/contas-categorias/typings";
import { OperacaoEnum } from "core/resources/enums/conta";
import colors from "core/resources/theme/colors";
import { useEffect, useState } from "react";
import { FaCheck } from "react-icons/fa";
import { Container } from "./styles";
import ModalPlanoContas from "./components/ModalPlanoContas";

export interface SelectContaCategoriaProps<T = any> extends TreeSelectProps<T> {
  value: any;
  onChange: (v: any, _item?: any) => void;
  isInvalid?: boolean;
  canSelectParentNodes?: boolean;
  operacao?: OperacaoEnum;
  idsToFilter?: string[];
  disableNotClassifiableNodes?: boolean;
  bg?: string;
}

const mapContasCategoriasToTreeNode = (
  contasCategorias: CategoriaFinanceiraResponse[],
  selectedContaCategoriaId?: string,
  filteredIds: string[] = [],
  disableNotClassifiableNodes?: boolean
): DataNode[] => {
  return contasCategorias.map((contaCategoria) => {
    const isLeaf = contaCategoria.items.length < 1;

    const isDisabledNode =
      (disableNotClassifiableNodes &&
        !isLeaf &&
        contaCategoria.podeClassificarConta === null) ||
      contaCategoria.podeClassificarConta === false;

    const isSelectable = !filteredIds.includes(contaCategoria.id);

    return {
      isLeaf,
      selectable: isSelectable,
      item: contaCategoria,
      title: `${contaCategoria.breadcrumb ?? ""} - ${contaCategoria.nome}`,
      key: contaCategoria.id,
      children: mapContasCategoriasToTreeNode(
        contaCategoria.items,
        selectedContaCategoriaId,
        filteredIds,
        disableNotClassifiableNodes
      ),
      switcherIcon:
        isLeaf && selectedContaCategoriaId === contaCategoria.id ? (
          <FaCheck
            fontSize={10}
            style={{ marginRight: 4 }}
            color={colors.green500}
          />
        ) : undefined,
      disabled: !isSelectable || isDisabledNode,
    };
  });
};

const getTreeKeys = (nodes: DataNode[]) => {
  const keys: (string | number)[] = [];

  nodes.forEach((node) => {
    keys.push(node.key);
    if (node.children?.length) {
      keys.push(...getTreeKeys(node.children));
    }
  });

  return keys;
};

function SelectContaCategoria({
  value,
  onChange,
  operacao,
  isInvalid,
  idsToFilter = [],
  bg,
  disableNotClassifiableNodes,
  ...rest
}: SelectContaCategoriaProps) {
  const { isLoading, data } = useGetContasCategorias({
    operacao,
    somenteAtivos: true,
  });

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [search, setSearch] = useState<string>();

  useEffect(() => {
    if (!isOpenModal) {
      setSearch("");
    }
  }, [isOpenModal]);

  const treeData = data
    ? mapContasCategoriasToTreeNode(
        data,
        value,
        idsToFilter,
        disableNotClassifiableNodes
      )
    : [];

  const treeKeys = getTreeKeys(treeData);

  return (
    <>
      <Container isInvalid={isInvalid} bg={bg}>
        <TreeSelect
          loading={isLoading}
          size="large"
          showSearch
          style={{
            width: "100%",
            borderColor: isInvalid ? "red" : "transparent",
            borderWidth: isInvalid ? "1px" : "0px",
            borderStyle: "solid",
          }}
          dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
          placeholder="Selecione a categoria"
          disabled={isLoading}
          allowClear={true}
          {...rest}
          treeDefaultExpandAll
          treeData={treeData}
          treeDefaultExpandedKeys={treeKeys}
          onChange={(v, label, extras) => {
            onChange(v, extras?.triggerNode?.props?.item);
          }}
          value={value || undefined}
          searchValue={search}
          onSearch={(v) => {
            if (!isOpenModal) {
              setSearch(v);
            }
          }}
          treeNodeFilterProp="title"
          notFoundContent={
            <Box>
              Nada encontrado!!{" "}
              <Button
                leftIcon={<i className="mdi mdi-plus" />}
                size="xs"
                colorScheme="orange"
                onClick={() => setIsOpenModal(true)}
              >
                cadastrar novo
              </Button>
            </Box>
          }
        />
      </Container>

      {isOpenModal ? (
        <ModalPlanoContas
          isOpen={isOpenModal}
          onClose={() => setIsOpenModal(false)}
        />
      ) : null}
    </>
  );
}

export default SelectContaCategoria;
